/**
 * The Central's WhatsApp channel rules (D636, doc 25 phase 1): who may be answered, how, and what a delivery receipt does.
 *
 *  - CONTACT CONSENT (messaging purpose). A customer who writes to a business number has started the conversation: that is recorded
 *    as an opt-in to being answered (`user_initiated`), at the version of the consent text. A message equal to one of the number's
 *    opt-out words withdraws it; the next message the customer sends starts it again. MARKETING is a separate flag that nothing here
 *    ever sets from an inbound message: it needs an explicit act of the customer.
 *  - THE OUTBOUND DECISION. Free text is allowed only inside the 24 h customer-service window that the customer's last message opens;
 *    outside it only an approved template goes. A number that is not ACTIVE, one WhatsApp rates RED, and a contact who opted out are
 *    refused first, each with a stable reason code. The decision is pure and fail-closed: the caller sends nothing on a refusal.
 *  - THE RECEIPT FOLD. Meta's statuses can arrive out of order or twice; a message only moves forward (queued -> sent -> delivered ->
 *    read), `failed` ends one that was not yet delivered, and nothing moves a message backwards.
 *  - RETRY. A failed send that the provider said is worth retrying is retried by an agent up to a bound, then it is dead-lettered
 *    (the existing retry/DLQ fold of this package: a broken send never loops forever).
 */
import { err, ok } from '@vello/core';
import { NUMBER_REFUSAL_CODES, normalizeKeyword } from './business-number.js';
import { isBlockedByQuality } from './delivery.js';
import { withinServiceWindow } from './messages.js';
import { cpLength, cpSlice, hasControl } from './plain-text.js';
export const WHATSAPP_CONSENT_VERSION = 'wa-central-1';
/**
 * How long a Meta event id is remembered by the dedupe ledger (days) - Meta retries a webhook for days, never longer. The retention job prunes the ledger
 * after this long, so an event older than this is dropped on arrival: a replay of a captured, validly signed request can never be admitted twice.
 */
export const LEDGER_MEMORY_DAYS = 14;
/** A message that stayed `queued` this long (ms) was probably lost between the Graph call and its record: the agent may send it again. */
export const STUCK_QUEUED_MS = 120_000;
export const NO_CONSENT = { optedIn: false, version: WHATSAPP_CONSENT_VERSION, marketingOptIn: false };
/** True when a message text is one of the number's opt-out words (compared whole, trimmed, case-insensitive). */
export function isOptOutText(text, keywords) {
    if (text === undefined)
        return false;
    const t = normalizeKeyword(text);
    return t !== '' && keywords.some((k) => normalizeKeyword(k) === t);
}
/** Fold one inbound message into the contact's consent. */
export function foldInboundConsent(state, text, optOutKeywords, at) {
    if (isOptOutText(text, optOutKeywords)) {
        if (state.optedOutAt !== undefined && !state.optedIn)
            return { state, optedOutNow: false, optedInNow: false };
        return { state: { ...state, optedIn: false, optedOutAt: at, marketingOptIn: false }, optedOutNow: true, optedInNow: false };
    }
    if (state.optedIn)
        return { state, optedOutNow: false, optedInNow: false };
    const { optedOutAt: _gone, ...rest } = state;
    return { state: { ...rest, optedIn: true, optInAt: at, optInBasis: 'user_initiated', version: WHATSAPP_CONSENT_VERSION }, optedOutNow: false, optedInNow: true };
}
export const OUTBOUND_REFUSAL_CODES = [
    'WA_NUMBER_NOT_ACTIVE',
    'WA_NUMBER_QUALITY_BLOCKED',
    'WA_CONTACT_OPTED_OUT',
    'WA_CONTACT_NOT_OPTED_IN',
    'WA_WINDOW_CLOSED_TEMPLATE_REQUIRED',
    'WA_MARKETING_OPT_IN_REQUIRED',
    'WA_TEMPLATE_NOT_APPROVED',
    'WA_TEMPLATE_PARAMS_INVALID',
];
export function decideOutbound(i) {
    if (i.number.status !== 'ACTIVE')
        return { ok: false, reasonCode: 'WA_NUMBER_NOT_ACTIVE' };
    if (isBlockedByQuality(i.number.qualityRating))
        return { ok: false, reasonCode: 'WA_NUMBER_QUALITY_BLOCKED' };
    if (i.consent.optedOutAt !== undefined && !i.consent.optedIn)
        return { ok: false, reasonCode: 'WA_CONTACT_OPTED_OUT' };
    if (!i.consent.optedIn)
        return { ok: false, reasonCode: 'WA_CONTACT_NOT_OPTED_IN' };
    if (i.mode === 'text') {
        if (i.lastInboundAt === undefined || !withinServiceWindow(i.lastInboundAt, i.now))
            return { ok: false, reasonCode: 'WA_WINDOW_CLOSED_TEMPLATE_REQUIRED' };
        return { ok: true, via: 'service_window' };
    }
    if (i.templateCategory === 'marketing' && !i.consent.marketingOptIn)
        return { ok: false, reasonCode: 'WA_MARKETING_OPT_IN_REQUIRED' };
    return { ok: true, via: 'template' };
}
/** When the free-text window closes (the customer's last message + 24 h), or undefined when they never wrote. */
export function serviceWindowClosesAt(lastInboundAt) {
    return lastInboundAt === undefined ? undefined : new Date(Date.parse(lastInboundAt) + 24 * 60 * 60 * 1000).toISOString();
}
// ---- templates ----------------------------------------------------------------------------------------------------------
const MAX_PARAM_CHARS = 200;
const FOUR_SPACES = / {4,}/;
/**
 * Check the parameters of a template send and render its preview into the text the timeline stores. Meta rejects a parameter with
 * a line break, a tab or four spaces in a row, so those are refused here instead of failing at the provider.
 */
export function renderTemplate(template, params) {
    const bad = (message) => err({ code: 'WA_TEMPLATE_PARAMS_INVALID', message });
    if (params.length !== template.paramCount)
        return bad(`${template.name} takes ${template.paramCount} parameter(s)`);
    const values = [];
    for (const p of params) {
        if (typeof p !== 'string')
            return bad('a parameter is text');
        const v = p.trim();
        if (v === '' || cpLength(v) > MAX_PARAM_CHARS || hasControl(v) || FOUR_SPACES.test(v))
            return bad('a parameter is 1..200 characters on one line');
        values.push(v);
    }
    const text = template.preview.replace(/\{\{(\d{1,2})\}\}/g, (_m, n) => values[Number(n) - 1] ?? '');
    return ok(cpSlice(text, 2000));
}
// ---- delivery receipts ----------------------------------------------------------------------------------------------------
export const OUTBOUND_STATUSES = ['queued', 'sent', 'delivered', 'read', 'failed'];
const RANK = { queued: 0, sent: 1, delivered: 2, read: 3 };
/**
 * The status a message moves to when a receipt arrives, or undefined when the receipt changes nothing (a duplicate, a late one for a
 * state already passed, anything after `failed`). `failed` only ends a message that was not yet delivered.
 */
export function foldReceipt(current, incoming) {
    if (current === 'failed' || current === 'read')
        return undefined;
    if (incoming === 'failed')
        return current === 'delivered' ? undefined : 'failed';
    return RANK[incoming] > RANK[current] ? incoming : undefined;
}
// ---- send failures ----------------------------------------------------------------------------------------------------------
export const SEND_FAILURE_CODES = [
    'WA_SEND_RATE_LIMITED',
    'WA_SEND_UNREACHABLE',
    'WA_SEND_WINDOW_CLOSED',
    'WA_SEND_NOT_AUTHORIZED',
    'WA_SEND_NOT_CONFIGURED',
    'WA_SEND_REJECTED',
    'WA_SEND_UNAVAILABLE',
    'WA_SEND_FAILED',
];
/** Map a Graph API error (HTTP status + Meta's numeric code) to a stable reason code and whether trying again can help. */
export function classifyGraphError(httpStatus, code) {
    if (code === 131026)
        return { reasonCode: 'WA_SEND_UNREACHABLE', retryable: false };
    if (code === 131047)
        return { reasonCode: 'WA_SEND_WINDOW_CLOSED', retryable: false };
    if (code === 130429 || code === 131048 || code === 131049 || code === 131056 || code === 4 || code === 80007 || httpStatus === 429)
        return { reasonCode: 'WA_SEND_RATE_LIMITED', retryable: true };
    if (code === 132000 || code === 132001 || code === 132012 || code === 132015 || code === 132016 || code === 133010 || code === 133005)
        return { reasonCode: 'WA_SEND_NOT_CONFIGURED', retryable: false };
    if (code === 190 || code === 10 || (code !== undefined && code >= 200 && code <= 299) || httpStatus === 401 || httpStatus === 403)
        return { reasonCode: 'WA_SEND_NOT_AUTHORIZED', retryable: false };
    if (code === 100 || code === 131009 || code === 131021)
        return { reasonCode: 'WA_SEND_REJECTED', retryable: false };
    if (httpStatus >= 500 || code === 1 || code === 2 || code === 131000 || code === 131016)
        return { reasonCode: 'WA_SEND_UNAVAILABLE', retryable: true };
    return { reasonCode: 'WA_SEND_FAILED', retryable: false };
}
// ---- the stable reason codes of the Central's WhatsApp channel --------------------------------------------------------------------------
/** What the conversation routes of the channel refuse with (beyond the number registry's, the outbound decision's and the send failures'). */
export const CHANNEL_REFUSAL_CODES = [
    'WA_NOT_A_WHATSAPP_CONVERSATION',
    'WA_CONTACT_ERASED',
    'WA_CREDENTIALS_MISSING',
    'WA_INTERACTION_ID_TAKEN',
    'WA_CONVERSATION_CLOSED',
    'WA_REPLY_REFUSED',
    'WA_CONCURRENT_CHANGE',
    'WA_MESSAGE_NOT_FOUND',
    'WA_RETRY_NOT_ALLOWED',
    'WA_TEXT_REQUIRED',
    'WA_TEMPLATE_REQUEST_INVALID',
    'WA_MEDIA_NOT_FOUND',
    'WA_MEDIA_TOO_LARGE',
];
/** Every reason code the channel can show a person: a refusal of the registry, of the outbound decision, of a conversation route, or why a send failed (`problem.central_whatsapp`). */
export const CENTRAL_WHATSAPP_REASON_CODES = [...NUMBER_REFUSAL_CODES, ...OUTBOUND_REFUSAL_CODES, ...SEND_FAILURE_CODES, ...CHANNEL_REFUSAL_CODES];
//# sourceMappingURL=central-channel.js.map